import axios from 'axios';

import axiosInstance from '../helpers/axios';

/**
 * Sign-up sheets (self-book slots) and the batch roster list.
 * Types live here rather than in client/types.ts so the two features stay
 * self-contained.
 */

// ── Shared ─────────────────────────────────────────────────────────────────

/** The API's message for an error, or a fallback — never a raw stack. */
export const apiMessage = (err: unknown, fallback = 'Something went wrong') => {
  if (axios.isAxiosError(err)) {
    const m = (err.response?.data as { message?: unknown } | undefined)
      ?.message;
    if (Array.isArray(m)) return m.join(' · ');
    if (typeof m === 'string' && m) return m;
  }
  return fallback;
};

export const fmtDay = (iso: string) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));

export const fmtTime = (iso: string) =>
  new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));

export const localZoneName = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

// ── Slot sheets ────────────────────────────────────────────────────────────

export type TSlotKind = 'prep-call' | 'coaching' | 'other';

export const SLOT_KINDS: { value: TSlotKind; label: string; hint: string }[] = [
  {
    value: 'prep-call',
    label: 'Prep call',
    hint: 'A booking sets this class’s prep call status to Scheduled and records the time.',
  },
  {
    value: 'coaching',
    label: '1:1 coaching',
    hint: 'A booking also adds the person to this class’s roster as a registration.',
  },
  {
    value: 'other',
    label: 'Other',
    hint: 'Just the sign-up sheet; nothing else changes.',
  },
];

export type TSlotBooking = {
  name: string;
  email: string;
  note: string | null;
  at: string;
  icsLink: string;
};

export type TSlotStaff = {
  id: string;
  start: string;
  end: string;
  capacity: number;
  remaining: number;
  bookings: TSlotBooking[];
};

export type TSlotSheet = {
  id: string;
  kind: TSlotKind;
  title: string;
  recordObject: 'class' | 'program';
  recordId: string;
  recordName: string | null;
  accountId: string | null;
  timeZone: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  closedAt: string | null;
  closed: boolean;
  publicLink: string;
  bookings: number;
  seats: number;
  slots: TSlotStaff[];
};

export type TSlotInput = {
  id?: string;
  start: string;
  end: string;
  capacity?: number;
};

export type TSlotSheetCreate = {
  kind: TSlotKind;
  title: string;
  recordObject: 'class' | 'program';
  recordId: string;
  timeZone?: string;
  slots: TSlotInput[];
  notes?: string;
};

export const createOpsSlotSheet = async (dto: TSlotSheetCreate) =>
  (await axiosInstance.post<TSlotSheet>('ops/slots', dto)).data;

export const getOpsSlotSheets = async (recordId: string) =>
  (
    await axiosInstance.get<{ recordId: string; items: TSlotSheet[] }>(
      'ops/slots',
      { params: { recordId } },
    )
  ).data;

export const getOpsSlotSheet = async (id: string) =>
  (await axiosInstance.get<TSlotSheet>(`ops/slots/${id}`)).data;

export const patchOpsSlotSheet = async (
  id: string,
  dto: { slots?: TSlotInput[]; notes?: string; closed?: boolean },
) => (await axiosInstance.patch<TSlotSheet>(`ops/slots/${id}`, dto)).data;

export const removeOpsSlotBooking = async (
  id: string,
  slotId: string,
  email: string,
) =>
  (
    await axiosInstance.delete<TSlotSheet>(
      `ops/slots/${id}/bookings/${encodeURIComponent(
        slotId,
      )}/${encodeURIComponent(email)}`,
    )
  ).data;

/**
 * The quick slot builder: a day, a window and a length → ISO slots. The
 * date and times are read in the browser's zone, which is where the staff
 * member is sitting; the sheet's time-zone label is just shown to the booker.
 */
export function buildSlots(input: {
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  lengthMinutes: number;
  gapMinutes?: number;
  capacity?: number;
}): TSlotInput[] {
  const start = new Date(`${input.date}T${input.startTime}`);
  const end = new Date(`${input.date}T${input.endTime}`);
  const length = Math.floor(Number(input.lengthMinutes));
  const gap = Math.max(0, Math.floor(Number(input.gapMinutes ?? 0)));
  const capacity = Math.max(1, Math.floor(Number(input.capacity ?? 1)) || 1);
  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    end <= start ||
    !Number.isFinite(length) ||
    length < 5
  ) {
    return [];
  }
  const out: TSlotInput[] = [];
  let cursor = start.getTime();
  while (cursor + length * 60_000 <= end.getTime() && out.length < 200) {
    out.push({
      start: new Date(cursor).toISOString(),
      end: new Date(cursor + length * 60_000).toISOString(),
      capacity,
    });
    cursor += (length + gap) * 60_000;
  }
  return out;
}

// ── Public (signed link, no login) ─────────────────────────────────────────

export type TPublicSlot = {
  id: string;
  start: string;
  end: string;
  remaining: number;
};

export type TPublicSheet = {
  id: string;
  kind: TSlotKind;
  title: string;
  notes: string | null;
  timeZone: string | null;
  closed: boolean;
  recordName: string | null;
  slots: TPublicSlot[];
};

export type TBookResult = {
  sheetId: string;
  slotId: string;
  start: string;
  end: string;
  timeZone: string | null;
  title: string;
  name: string;
  email: string;
  moved: boolean;
  icsLink: string;
  registration: {
    ok: boolean;
    id?: string;
    status?: number;
    message?: string;
  } | null;
};

export const getPublicSlotSheet = async (id: string, sig: string) =>
  (
    await axiosInstance.get<TPublicSheet>(
      `public/slots/${encodeURIComponent(id)}`,
      { params: { sig } },
    )
  ).data;

export const bookPublicSlot = async (
  id: string,
  sig: string,
  dto: { slotId: string; name: string; email: string; note?: string },
) =>
  (
    await axiosInstance.post<TBookResult>(
      `public/slots/${encodeURIComponent(id)}/book`,
      dto,
      { params: { sig } },
    )
  ).data;

// ── Batch roster list ──────────────────────────────────────────────────────

export type TBatchAction = 'add' | 'cancel' | 'waitlist';
export const BATCH_ACTIONS: TBatchAction[] = ['add', 'cancel', 'waitlist'];

export type TBatchParsedRow = {
  line: number;
  action: TBatchAction;
  firstName: string | null;
  lastName: string | null;
  email: string;
  problem: string | null;
};

export type TBatchParsed = {
  rows: TBatchParsedRow[];
  columns: string[];
  headerDetected: boolean;
  delimiter: string;
  skipped: { line: number; reason: string }[];
};

export type TBatchRow = {
  action: TBatchAction;
  firstName?: string;
  lastName?: string;
  email: string;
  programId?: string;
};

export type TBatchOutcome = {
  classId: string;
  className: string | null;
  ok: boolean;
  status: number;
  registrationId: string | null;
  message: string | null;
};

export type TBatchResult = {
  classId: string | null;
  seriesId: string | null;
  classes: { id: string; name: string | null }[];
  totals: {
    added: number;
    waitlisted: number;
    cancelled: number;
    refused: number;
  };
  rows: {
    index: number;
    action: string;
    email: string;
    name: string | null;
    ok: boolean;
    outcomes: TBatchOutcome[];
  }[];
};

export const parseOpsRosterCsv = async (csv: string) =>
  (
    await axiosInstance.post<TBatchParsed>('ops/registrations/batch/parse', {
      csv,
    })
  ).data;

export const applyOpsRosterBatch = async (dto: {
  classId?: string;
  seriesId?: string;
  rows: TBatchRow[];
}) =>
  (await axiosInstance.post<TBatchResult>('ops/registrations/batch', dto)).data;
